import React, { useCallback, useEffect, useState } from "react";
import * as PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import localizationKeys from "../../../i18n/localizationKeys.js";
import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useDispatch, useSelector } from "react-redux";
import {
  getDataDuration,
  getDurationOption,
  getElectricDataDuration,
  getSepDataDuration,
  setDurationOption,
  setElectricStartDate,
  setElectricEndDate,
  setEndDate,
  setSepStartDate,
  setSepEndDate,
  setStartDate,
  getCustomDataDuration,
  setCustomDataStartDate,
  setCustomDataEndDate,
  resetDurationOption,
} from "../../../model/slice/dataDurationSlice.js";
import { DURATION, DURATION_OPTION_TYPE } from "../calendarDuration/durationOption.js";
import { ENGINE_POSITION } from "../../../constants/trendGraph/enginesLine.js";
import { selectedChartType } from "../../../model/slice/chartTypeSlice.js";
import { MUIDateTimePicker } from "../../dateTimePicker/dateTimePicker.jsx";
import { Stack } from "@mui/system";
import { DATE_FORMAT, VESSEL_TYPE } from "../../../constants/constants.js";
import dayjs from "dayjs";

const DataDuration = React.memo(({ type, t, position, vesselId, currentVessel }) => {
  const dispatch = useDispatch();
  const dataDurationState = useSelector(getDataDuration);
  const electricDurationState = useSelector(getElectricDataDuration);
  const sepDurationState = useSelector(getSepDataDuration);
  const customDurationState = useSelector(getCustomDataDuration);
  const durationOption = useSelector(getDurationOption);
  const selectedChart = useSelector(selectedChartType);
  const [firstRender, setFirstRender] = useState(true);

  const setDurationData = (duration) => {
    switch (true) {
      case type === DURATION.ENGINE && position === ENGINE_POSITION.PORT:
        dispatch(
          setDurationOption({
            option: DURATION_OPTION_TYPE.PORT,
            value: duration,
            vesselId,
          })
        );
        break;
      case type === DURATION.ENGINE && position === ENGINE_POSITION.STARBOARD:
        dispatch(
          setDurationOption({
            option: DURATION_OPTION_TYPE.STARBOARD,
            value: duration,
            vesselId,
          })
        );
        break;
      case type === DURATION.ENGINE && position === ENGINE_POSITION.CENTER:
        dispatch(
          setDurationOption({
            option: DURATION_OPTION_TYPE.CENTER,
            value: duration,
            vesselId,
          })
        );
        break;
      case type === DURATION.SEP:
        dispatch(
          setDurationOption({
            option: DURATION_OPTION_TYPE.SEP,
            value: duration,
            vesselId,
          })
        );
        break;
      case type === DURATION.ELECTRIC:
        dispatch(
          setDurationOption({
            option: DURATION_OPTION_TYPE.ELECTRIC,
            value: duration,
            vesselId,
          })
        );
        break;
      case type === DURATION.CUSTOM:
        dispatch(
          setDurationOption({
            option: DURATION_OPTION_TYPE.CUSTOM,
            value: duration,
            vesselId,
          })
        );
        break;
    }
  };

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
      return;
    }
    dispatch(resetDurationOption());
  }, [vesselId, type, selectedChart]);

  const currentDurationOption = useCallback(() => {
    switch (true) {
      case type === DURATION.ENGINE && position === ENGINE_POSITION.PORT:
        return durationOption.port;
      case type === DURATION.ENGINE && position === ENGINE_POSITION.STARBOARD:
        return durationOption.starboard;
      case type === DURATION.ENGINE && position === ENGINE_POSITION.CENTER:
        return durationOption.center;
      case type === DURATION.ELECTRIC:
        return durationOption.electric;
      case type === DURATION.SEP:
        return durationOption.sep;
      case type === DURATION.CUSTOM:
        return durationOption.custom;
    }
  }, [type, position, vesselId, durationOption]);

  const setDurationStartDate = (d) => {
    const date = dayjs.utc(d).startOf('minute').format(DATE_FORMAT);
    switch (type) {
      case DURATION.ENGINE:
        dispatch(setStartDate(date));
        break;
      case DURATION.SEP:
        dispatch(setSepStartDate(date));
        break;
      case DURATION.ELECTRIC:
        dispatch(setElectricStartDate(date));
        break;
      case DURATION.CUSTOM:
        dispatch(setCustomDataStartDate(date));
        break;
    }
  };

  const setDurationEndDate = (d) => {
    const date = dayjs.utc(d).startOf('minute').format(DATE_FORMAT);
    switch (type) {
      case DURATION.ENGINE:
        dispatch(setEndDate(date));
        break;
      case DURATION.SEP:
        dispatch(setSepEndDate(date));
        break;
      case DURATION.ELECTRIC:
        dispatch(setElectricEndDate(date));
        break;
      case DURATION.CUSTOM:
        dispatch(setCustomDataEndDate(date));
        break;
    }
  };

  useEffect(() => {
    const currentDuration = currentDurationOption();
    if (!currentDuration?.vesselId || currentDuration?.vesselId !== vesselId) {
      setDurationData(currentDuration?.value);
    }
  }, [vesselId]);

  const selectedDate = () => {
    switch (true) {
      case type === DURATION.ENGINE:
        return dataDurationState;
      case type === DURATION.ELECTRIC:
        return electricDurationState;
      case type === DURATION.SEP:
        return sepDurationState;
      case type === DURATION.CUSTOM:
        return customDurationState;
      default:
        return null;
    }
  };
  
  return (
        <Stack direction="row" spacing={2} sx={{width: '100%', paddingTop: "8px"}}>
          <Grid>
            <Typography color="white" fontSize={12}>{t(localizationKeys.StartTime)}</Typography>
            <MUIDateTimePicker isSEPVessel={currentVessel?.vesselType === VESSEL_TYPE.SEP} date={selectedDate()?.startDate} setDateFunction={(d) => setDurationStartDate(d)} isStart={true} limitationDate={selectedDate()?.endDate}/>
          </Grid>
          <Grid>
            <Typography color="white" fontSize={12}>{t(localizationKeys.EndTime)}</Typography>
            <MUIDateTimePicker isSEPVessel={currentVessel?.vesselType === VESSEL_TYPE.SEP} date={selectedDate()?.endDate} setDateFunction={(d) => setDurationEndDate(d)} isStart={false} limitationDate={selectedDate()?.startDate}/>
          </Grid>
        </Stack>
  );
});

DataDuration.propTypes = {
  type: PropTypes.number,
  t: PropTypes.func,
  minDate: PropTypes.string,
  vesselId: PropTypes.string,
  position: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  currentVessel: PropTypes.object,
};

export default withTranslation()(DataDuration);
